/**
 * AddServiceToGuestDialog — book a catalog service onto a guest.
 *
 * Two steps: pick a service from the catalog, then pick *when*. Scheduled and
 * recurring services resolve to one of their upcoming runs; every other booking
 * mode (one-off, on-request, per-night, rental) takes a plain date. The stay
 * window seeds the defaults. Overlaps against the guest's existing schedule are
 * surfaced as a soft warning, not a block — the operator decides.
 *
 * Settlement mirrors the backend: with a booking on file and a price, the charge
 * lands on checkout; otherwise nothing is charged here.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from '@/core';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import { Input } from '@/theme/components/ui/input';
import { Badge } from '@/theme/components/ui/badge';
import { LoadingState } from '@/shared/components';
import { useLocationCurrency } from '@/shared/hooks/useLocationCurrency';
import { ChevronLeft, Search, AlertTriangle, CalendarClock, ArrowRight } from 'lucide-react';
import { useServiceProducts } from '../hooks/useServiceProducts';
import { useServiceProductRuns } from '../hooks/useServiceRuns';
import { useCreateServiceBooking } from '../hooks/useServiceBookings';
import { findScheduleConflicts } from '../utils/scheduleConflicts';
import type { ServiceProductListItem, ServiceRunListItem, GuestScheduleItem } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestId: string;
  bookingId?: string | null;
  /** Stay window — seeds the on-demand date and the run search range. */
  checkIn?: string | null;
  checkOut?: string | null;
  /** The guest's current schedule, for overlap detection. */
  schedule: GuestScheduleItem[];
  /** Preselect a catalog service (catalog drawer's "Assign to guest"). */
  initialProduct?: ServiceProductListItem | null;
}

/** YYYY-MM-DD n days from an anchor (defaults to today). */
function isoOffset(days: number, anchor?: string | null): string {
  const base = anchor ? new Date(`${anchor}T00:00:00`) : new Date();
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function needsRun(mode: ServiceProductListItem['bookingMode']): boolean {
  return mode === 'scheduled' || mode === 'recurring';
}

export function AddServiceToGuestDialog({
  open, onOpenChange, guestId, bookingId, checkIn, checkOut, schedule, initialProduct,
}: Props) {
  const { t } = useTranslation();
  const { formatPrice } = useLocationCurrency();

  const [search, setSearch] = useState('');
  const [product, setProduct] = useState<ServiceProductListItem | null>(initialProduct ?? null);
  const [runId, setRunId] = useState<string | null>(null);
  const [serviceDate, setServiceDate] = useState<string>('');

  const { data: products = [], isLoading: loadingProducts } = useServiceProducts(
    { search: search || undefined, isActive: true },
    { enabled: open && !product },
  );

  const runFrom = checkIn && checkIn > isoOffset(0) ? checkIn : isoOffset(0);
  const runTo = checkOut ?? isoOffset(21);
  const { data: runs = [], isLoading: loadingRuns } = useServiceProductRuns(
    product && needsRun(product.bookingMode) ? product.id : undefined,
    runFrom, runTo,
    { enabled: open && !!product && needsRun(product.bookingMode) },
  );

  const createBooking = useCreateServiceBooking();

  const selectedRun = useMemo(
    () => runs.find((r) => r.id === runId) ?? null,
    [runs, runId],
  );

  const runConflicts = (run: ServiceRunListItem) =>
    findScheduleConflicts({ date: run.date, startTime: run.startTime, endTime: run.endTime }, schedule);

  const activeConflicts = useMemo(() => {
    if (!product) return [];
    if (needsRun(product.bookingMode)) {
      return selectedRun ? runConflicts(selectedRun) : [];
    }
    if (!serviceDate) return [];
    return findScheduleConflicts({ date: serviceDate, startTime: null }, schedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, selectedRun, serviceDate, schedule]);

  const reset = () => { setProduct(null); setRunId(null); setServiceDate(''); setSearch(''); };
  const close = () => { onOpenChange(false); reset(); };

  const pickProduct = (p: ServiceProductListItem) => {
    setProduct(p);
    setRunId(null);
    setServiceDate(needsRun(p.bookingMode) ? '' : (checkIn ?? isoOffset(0)));
  };

  const canConfirm = !!product && (needsRun(product.bookingMode) ? !!runId : !!serviceDate);
  const willCharge = !!bookingId && !!product && Number(product.basePrice) > 0;

  const confirm = () => {
    if (!product || !canConfirm) return;
    const useRun = needsRun(product.bookingMode);
    createBooking.mutate(
      {
        product: product.id,
        run: useRun ? runId : null,
        guest: guestId,
        booking: bookingId ?? null,
        serviceDate: useRun ? null : serviceDate,
        quantity: 1,
        bookedVia: 'staff',
      },
      { onSuccess: close },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {product && (
              <button
                type="button"
                onClick={reset}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('common.back', 'Back')}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            {product ? product.name : t('services.add.title', 'Add a service')}
          </DialogTitle>
        </DialogHeader>

        {!product ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('services.add.searchPlaceholder', 'Search the catalog…')}
                className="pl-8"
              />
            </div>
            {loadingProducts ? (
              <LoadingState />
            ) : products.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('services.add.noProducts', 'No services match.')}
              </p>
            ) : (
              <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                {products.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickProduct(p)}
                    className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t(`services.serviceTypes.${p.serviceType}`, p.serviceType)}
                        {' · '}
                        {t(`services.bookingModes.${p.bookingMode}`, p.bookingMode)}
                      </p>
                    </div>
                    <span className="font-mono text-sm font-semibold tabular-nums">
                      {formatPrice(Number(p.basePrice))}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {needsRun(product.bookingMode) ? (
              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  {t('services.add.pickRun', 'Pick a run')}
                </p>
                {loadingRuns ? (
                  <LoadingState />
                ) : runs.length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('services.add.noRuns', 'No upcoming runs in the stay window.')}
                  </p>
                ) : (
                  <div className="max-h-64 space-y-1.5 overflow-y-auto">
                    {runs.map((r) => {
                      const conflicts = runConflicts(r);
                      const selected = r.id === runId;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setRunId(r.id)}
                          className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                            selected ? 'border-ocean-700 bg-ocean-700/5' : 'border-border hover:border-sand-400'
                          }`}
                        >
                          <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">
                              {r.date} · {r.startTime.slice(0, 5)}
                              {r.endTime ? `–${r.endTime.slice(0, 5)}` : ''}
                            </p>
                            {(r.origin || r.dest) && (
                              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                {r.origin}
                                {r.origin && r.dest && <ArrowRight className="h-3 w-3" />}
                                {r.dest}
                              </p>
                            )}
                          </div>
                          {conflicts.length > 0 && (
                            <Badge variant="outline" className="gap-1 text-sun-700">
                              <AlertTriangle className="h-3 w-3" />
                              {t('services.add.overlap', 'Overlap')}
                            </Badge>
                          )}
                          {r.capacity != null && (
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {t('services.add.cap', 'cap {{n}}', { n: r.capacity })}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <label htmlFor="svc-date" className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  {t('services.add.date', 'Date')}
                </label>
                <Input
                  id="svc-date"
                  type="date"
                  value={serviceDate}
                  min={checkIn ?? undefined}
                  max={checkOut ?? undefined}
                  onChange={(e) => setServiceDate(e.target.value)}
                />
              </div>
            )}

            {activeConflicts.length > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-sun-300 bg-sun-50 p-3 text-sm text-sun-800 dark:bg-sun-950/30">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">{t('services.add.conflictTitle', 'Schedule overlap')}</p>
                  <p className="text-xs">
                    {t('services.add.conflictDescription', 'Overlaps {{items}}. You can still book it.', {
                      items: activeConflicts.map((c) => c.title).join(', '),
                    })}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">
                {willCharge
                  ? t('services.add.chargeAtCheckout', 'Charged at checkout')
                  : t('services.add.noCharge', 'No charge')}
              </span>
              <span className="font-mono font-semibold tabular-nums">{formatPrice(Number(product.basePrice))}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>{t('common.cancel', 'Cancel')}</Button>
          {product && (
            <Button onClick={confirm} disabled={!canConfirm || createBooking.isPending}>
              {t('services.add.confirm', 'Add to guest')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
